// Bending a straight conveyor through a quarter turn.
//
// The Kenney kit has no curved piece, so a corner used to be two straight
// parts meeting at a right angle — which reads as two belts that happen to
// touch rather than as one lane that turns. The fix is to bend the straight
// mesh rather than model a new one: the slats, the thickness and the material
// all come along, and the UVs stay attached to their vertices so the texture
// fans on the outside of the turn the way a real curved belt does.
//
// The straight geometry runs along facing 0 (north, -Z) after `models.ts`
// rotates it, and occupies one square. A bend maps the run onto a quarter arc
// of radius 1/2 centred on the corner of the square, so the lane enters at the
// middle of one edge and leaves at the middle of the next — which is exactly
// where the straight parts either side of it meet.

import { BufferAttribute, BufferGeometry } from "three";

/**
 * How tight the inside of a turn is allowed to get.
 *
 * Small enough not to change the shape, large enough that the innermost
 * vertices do not all land on the same point.
 */
const INNER_RADIUS = 0.06;

/**
 * How many times the deck is quartered before bending.
 *
 * Three puts eight segments around the quarter turn, which is under twelve
 * degrees a facet — past the point where the silhouette reads as flat sides.
 * It costs 92 triangles becoming ~5900, which matters not at all: a board has
 * a handful of corners on it, and the mesh is built once at load.
 */
const BEND_SUBDIVISIONS = 3;

/** Which way the lane turns, seen from above: right is clockwise. */
export type Hand = "right" | "left";

/**
 * A quarter-turn version of `straight`.
 *
 * At zero yaw the result enters travelling north and leaves travelling east
 * (`right`) or west (`left`), so a corner is placed by yawing to its *inbound*
 * direction rather than its outbound one.
 */
export function bendConveyor(straight: BufferGeometry, hand: Hand): BufferGeometry {
  // Tessellate first. The kit's conveyor is 92 triangles — about two segments
  // along its length — and bending two segments through ninety degrees gives a
  // wedge, not an arc. Each round quarters every triangle, so three rounds put
  // eight segments around the turn and the deck reads as curved.
  //
  // Shape-preserving on the way in: every new vertex is the midpoint of an
  // edge, so on flat faces it lands exactly where the surface already was.
  let geometry = straight.toNonIndexed();
  for (let round = 0; round < BEND_SUBDIVISIONS; round++) {
    const finer = subdivide(geometry);
    geometry.dispose();
    geometry = finer;
  }
  const position = geometry.getAttribute("position");
  const normal = geometry.getAttribute("normal");

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    const y = position.getY(i);
    const z = position.getZ(i);

    // How far along the run this vertex is: the tail sits at +Z, the head at
    // -Z. Clamped because a mesh may overhang its square very slightly, and a
    // vertex past the end should ride the end of the arc rather than carry on
    // round it.
    const along = Math.min(1, Math.max(0, 0.5 - z));
    const angle = along * (Math.PI / 2);
    const sin = Math.sin(angle);
    const cos = Math.cos(angle);

    // The arc's centre is the square's north-east corner, so a vertex further
    // toward +X is nearer the centre and travels a shorter path — which is
    // what makes the inside of the turn tighter than the outside.
    //
    // Clamped away from zero. The belt is as wide as the square, so the inside
    // edge would otherwise sweep a radius of exactly nothing and collapse to a
    // point — a spike at the inside of every corner. A small floor rounds it
    // off without moving the outer edge, so a bend still meets the straight
    // parts either side of it along their full width.
    const radius = Math.max(INNER_RADIUS, 0.5 - x);
    position.setXYZ(i, 0.5 - radius * cos, y, 0.5 - radius * sin);

    // The same rotation applied to the normal, rather than recomputing normals
    // afterwards: recomputing would smooth the deck's hard edges into the
    // frame and the part would stop looking like the straight one beside it.
    const nx = normal.getX(i);
    const nz = normal.getZ(i);
    normal.setXYZ(i, nx * cos - nz * sin, normal.getY(i), nx * sin + nz * cos);
  }

  if (hand === "left") mirrorX(geometry);

  position.needsUpdate = true;
  normal.needsUpdate = true;
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * The other hand of an authored corner.
 *
 * A pack may ship a real curved part instead of a bent straight one, and it
 * ships **one** — the two hands are the same casting seen in a mirror, so
 * modelling both would be two files that have to agree. Clones first, because
 * the caller keeps the original for the other hand.
 */
export function mirrorConveyor(corner: BufferGeometry): BufferGeometry {
  const flipped = corner.clone();
  mirrorX(flipped);
  flipped.computeBoundingSphere();
  return flipped;
}

/**
 * Split every triangle into four, on its edge midpoints.
 *
 * Uniform rather than adaptive: subdividing only the triangles that need it
 * leaves a T-junction wherever a split triangle meets an unsplit neighbour,
 * and a T-junction is a hairline crack you can see straight through. Doing
 * every triangle costs vertices and cannot crack.
 *
 * Interpolates whatever attributes the mesh has — the kit's conveyor carries
 * a tangent and a UV as well as a normal, and dropping either would strip the
 * texture off the bend.
 */
function subdivide(geometry: BufferGeometry): BufferGeometry {
  const names = Object.keys(geometry.attributes);
  const source = names.map((name) => geometry.getAttribute(name));
  const triangles = source[0].count / 3;
  const output = source.map(
    (attribute) => new Float32Array(triangles * 12 * attribute.itemSize)
  );

  let written = 0;
  /** Copy a source vertex, or the midpoint of two, into the next slot. */
  const put = (a: number, b: number): void => {
    for (let i = 0; i < names.length; i++) {
      const attribute = source[i];
      const size = attribute.itemSize;
      const target = output[i];
      const at = written * size;
      for (let c = 0; c < size; c++) {
        target[at + c] =
          (attribute.array[a * size + c] + attribute.array[b * size + c]) / 2;
      }
      // A midpoint of two unit normals is not a unit vector. Left unnormalised
      // it darkens the shading along every new edge.
      if (names[i] === "normal") {
        const length =
          Math.hypot(target[at], target[at + 1], target[at + 2]) || 1;
        target[at] /= length;
        target[at + 1] /= length;
        target[at + 2] /= length;
      }
    }
    written++;
  };

  for (let t = 0; t < triangles; t++) {
    const [a, b, c] = [t * 3, t * 3 + 1, t * 3 + 2];
    // Corner, midpoint, midpoint — three times — and the middle triangle last.
    // Same winding as the parent, so the whole mesh keeps facing outward.
    put(a, a); put(a, b); put(c, a);
    put(a, b); put(b, b); put(b, c);
    put(c, a); put(b, c); put(c, c);
    put(a, b); put(b, c); put(c, a);
  }

  const out = new BufferGeometry();
  names.forEach((name, i) => {
    out.setAttribute(name, new BufferAttribute(output[i], source[i].itemSize));
  });
  return out;
}

/**
 * Reflect the geometry across the X axis, winding included.
 *
 * A left turn is a right turn seen in a mirror, and a mirror reverses the
 * order a triangle's corners are given in. Without flipping the index buffer
 * every face would be inside out — back-face culling would hide the deck and
 * show its underside instead, which looks like the part failed to load.
 */
function mirrorX(geometry: BufferGeometry): void {
  const position = geometry.getAttribute("position");
  const normal = geometry.getAttribute("normal");
  for (let i = 0; i < position.count; i++) {
    position.setX(i, -position.getX(i));
    normal.setX(i, -normal.getX(i));
  }

  const index = geometry.getIndex();
  if (index) {
    for (let i = 0; i < index.count; i += 3) {
      const a = index.getX(i);
      index.setX(i, index.getX(i + 2));
      index.setX(i + 2, a);
    }
    index.needsUpdate = true;
    return;
  }

  // No index to reverse — subdividing produces a non-indexed mesh — so the
  // vertices themselves swap. Missing this case is invisible until you look at
  // a left-hand corner, which is then lit from inside and reads as a hole.
  for (const name of Object.keys(geometry.attributes)) {
    const attribute = geometry.getAttribute(name);
    const size = attribute.itemSize;
    for (let i = 0; i < attribute.count; i += 3) {
      for (let c = 0; c < size; c++) {
        const first = (i + 1) * size + c;
        const second = (i + 2) * size + c;
        const held = attribute.array[first];
        (attribute.array as Float32Array)[first] = attribute.array[second];
        (attribute.array as Float32Array)[second] = held;
      }
    }
    attribute.needsUpdate = true;
  }
}
